import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useProtocolFeeSchedules } from "@/hooks/useProtocolFeeSchedules";
import { useProtocolConfiguration } from "@/hooks/useProtocolConfiguration";
import { useBillableEvents } from "@/hooks/useBillableEvents";
import { DollarSign, Plus, Settings, Calendar, TrendingUp, Activity, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TooltipHelp } from "@/components/ui/tooltip-help";
import { BudgetImportDialog } from "@/components/billing/BudgetImportDialog";

interface TrialProtocolBillingManagerProps {
  trialId: string;
  trialSiteId: string;
  trialTitle: string;
  autoOpenWizard?: boolean;
  onWizardClosed?: () => void;
}

interface FeeItem {
  id: string;
  enabled: boolean;
  name: string;
  amount: string;
  eventTrigger: string;
  category: string;
}

const DEFAULT_VISITS: FeeItem[] = [
  { id: "v1", enabled: true, name: "Screening Visit", amount: "", eventTrigger: "screening_visit", category: "visit" },
  { id: "v2", enabled: true, name: "Baseline Visit", amount: "", eventTrigger: "baseline_visit", category: "visit" },
  { id: "v3", enabled: false, name: "Week 4 Follow-up", amount: "", eventTrigger: "week_4_visit", category: "visit" },
  { id: "v4", enabled: false, name: "Week 8 Follow-up", amount: "", eventTrigger: "week_8_visit", category: "visit" },
  { id: "v5", enabled: false, name: "Month 6 Follow-up", amount: "", eventTrigger: "month_6_visit", category: "visit" },
  { id: "v6", enabled: false, name: "End of Study Visit", amount: "", eventTrigger: "end_of_study_visit", category: "visit" },
];

const DEFAULT_MILESTONES: FeeItem[] = [
  { id: "m1", enabled: true, name: "Enrollment Bonus", amount: "", eventTrigger: "enrollment_milestone", category: "milestone" },
  { id: "m2", enabled: false, name: "Randomization Fee", amount: "", eventTrigger: "randomization_milestone", category: "milestone" },
  { id: "m3", enabled: false, name: "Study Completion", amount: "", eventTrigger: "completion_milestone", category: "milestone" },
];

const DEFAULT_PROCEDURES: FeeItem[] = [
  { id: "p1", enabled: false, name: "Lab Work", amount: "", eventTrigger: "manual_only", category: "procedure" },
  { id: "p2", enabled: false, name: "ECG", amount: "", eventTrigger: "manual_only", category: "procedure" },
  { id: "p3", enabled: false, name: "MRI Scan", amount: "", eventTrigger: "manual_only", category: "procedure" },
];

const DEFAULT_PER_DIEM: FeeItem[] = [
  { id: "d1", enabled: false, name: "Patient Per Diem", amount: "", eventTrigger: "manual_only", category: "per_diem" },
  { id: "d2", enabled: false, name: "Screen Failure Fee", amount: "", eventTrigger: "withdrawn_milestone", category: "per_diem" },
];

export function TrialProtocolBillingManager({ 
  trialId, 
  trialSiteId, 
  trialTitle,
  autoOpenWizard,
  onWizardClosed 
}: TrialProtocolBillingManagerProps) {
  const { data: schedules = [] } = useProtocolFeeSchedules(trialId);
  const { data: eventsData } = useBillableEvents({ trialSiteId });
  const { saveProtocolConfiguration, isSaving } = useProtocolConfiguration();
  const { toast } = useToast();

  const [showProtocolWizard, setShowProtocolWizard] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showBudgetImport, setShowBudgetImport] = useState(false);

  useEffect(() => {
    if (autoOpenWizard && schedules.length === 0) {
      setShowProtocolWizard(true);
    }
  }, [autoOpenWizard, schedules.length]);

  const handleWizardClose = (open: boolean) => {
    setShowProtocolWizard(open);
    if (!open && onWizardClosed) {
      onWizardClosed();
    }
  };
  
  // Protocol wizard state
  const [visits, setVisits] = useState<FeeItem[]>(DEFAULT_VISITS);
  const [milestones, setMilestones] = useState<FeeItem[]>(DEFAULT_MILESTONES);
  const [procedures, setProcedures] = useState<FeeItem[]>(DEFAULT_PROCEDURES);
  const [perDiem, setPerDiem] = useState<FeeItem[]>(DEFAULT_PER_DIEM);
  const [customItems, setCustomItems] = useState<FeeItem[]>([]);

  // Add event state
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedFeeScheduleId, setSelectedFeeScheduleId] = useState("");
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const [patients, setPatients] = useState<any[]>([]);
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);

  const events: any[] = Array.isArray(eventsData) ? eventsData : (eventsData?.events || []);
  const trialEvents = events.filter((e: any) => e.fee_schedule?.trial_id === trialId);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'visit': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'procedure': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'milestone': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'per_diem': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const updateItem = (items: FeeItem[], setItems: (items: FeeItem[]) => void, id: string, field: keyof FeeItem, value: any) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addCustomItem = (category: string) => {
    const newId = `custom_${Date.now()}`;
    const newItem: FeeItem = {
      id: newId,
      enabled: true,
      name: "",
      amount: "",
      eventTrigger: category === "visit" ? "custom_visit" : category === "milestone" ? "custom_milestone" : "manual_only",
      category,
    };
    setCustomItems([...customItems, newItem]);
  };

  const handleProtocolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const allItems = [...visits, ...milestones, ...procedures, ...perDiem, ...customItems]
      .filter(item => item.enabled && item.amount && parseFloat(item.amount) > 0);

    if (allItems.length === 0) {
      toast({
        title: "No fees configured",
        description: "Please enable and set amounts for at least one fee item.",
        variant: "destructive",
      });
      return;
    }

    const feeSchedules = allItems.map(item => ({
      fee_name: item.name,
      fee_description: `${item.name} - Auto-triggered on ${item.eventTrigger.replace(/_/g, ' ')}`,
      fee_category: item.category,
      amount: parseFloat(item.amount),
      event_trigger_type: item.eventTrigger,
    }));

    await saveProtocolConfiguration({
      trialId,
      feeSchedules,
    }, {
      onSuccess: () => {
        toast({
          title: "Protocol configured",
          description: `Successfully configured ${feeSchedules.length} billable events for ${trialTitle}`,
        });
        setShowProtocolWizard(false);
      },
    });
  };

  const loadTrialPatients = async () => {
    const { data } = await supabase
      .from('patient_trial_status')
      .select(`
        patient_id,
        trial_site_id,
        profile:profiles!patient_trial_status_patient_id_fkey(first_name, last_name)
      `)
      .eq('trial_id', trialId)
      .in('status', ['screening', 'enrolled', 'active']);

    setPatients(data || []);
  };

  const handleAddEventClick = () => {
    loadTrialPatients();
    setShowAddEvent(true);
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPatientId || !selectedFeeScheduleId) {
      toast({
        title: "Missing Information",
        description: "Please select both patient and fee item",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingEvent(true);

    try {
      const selectedFee = schedules.find(f => f.id === selectedFeeScheduleId);
      if (!selectedFee) throw new Error("Fee schedule not found");

      const qty = parseInt(quantity) || 1;
      const totalAmount = selectedFee.amount * qty;

      const { error } = await supabase
        .from('billable_events')
        .insert({
          trial_site_id: trialSiteId,
          patient_id: selectedPatientId,
          fee_schedule_id: selectedFeeScheduleId,
          event_date: eventDate,
          unit_amount: selectedFee.amount,
          quantity: qty,
          total_amount: totalAmount,
          currency: selectedFee.currency || 'USD',
          status: 'pending',
          notes: notes || `Manual entry: ${selectedFee.fee_name}`,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Billable event created successfully",
      });

      setSelectedPatientId("");
      setSelectedFeeScheduleId("");
      setEventDate(new Date().toISOString().split('T')[0]);
      setQuantity("1");
      setNotes("");
      setShowAddEvent(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create billable event",
        variant: "destructive",
      });
    } finally {
      setIsLoadingEvent(false);
    }
  };

  const calculateTotal = () => {
    const allItems = [...visits, ...milestones, ...procedures, ...perDiem, ...customItems];
    const total = allItems
      .filter(item => item.enabled && item.amount)
      .reduce((sum, item) => sum + parseFloat(item.amount || "0"), 0);
    const count = allItems.filter(item => item.enabled && item.amount && parseFloat(item.amount) > 0).length;
    return { total, count };
  };

  const renderFeeSection = (
    title: string,
    description: string,
    icon: React.ReactNode,
    items: FeeItem[],
    setItems: (items: FeeItem[]) => void,
    category: string
  ) => (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg border bg-background">
            <Checkbox
              checked={item.enabled}
              onCheckedChange={(checked) => updateItem(items, setItems, item.id, "enabled", checked)}
            />
            <div className="flex-1 grid grid-cols-3 gap-2">
              <Input
                value={item.name}
                onChange={(e) => updateItem(items, setItems, item.id, "name", e.target.value)}
                placeholder="Fee name"
                disabled={!item.enabled}
                className="col-span-1"
              />
              <Input
                type="number"
                step="0.01"
                value={item.amount}
                onChange={(e) => updateItem(items, setItems, item.id, "amount", e.target.value)}
                placeholder="Amount"
                disabled={!item.enabled}
                className="col-span-1"
              />
              <div className="col-span-1 text-sm text-muted-foreground flex items-center">
                {item.eventTrigger === "manual_only" ? "Manual billing" : `Triggers: ${item.eventTrigger.replace(/_/g, ' ')}`}
              </div>
            </div>
          </div>
        ))}
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addCustomItem(category)}
          className="w-full"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Custom {title.split(' ')[0]}
        </Button>
      </div>
    </Card>
  );

  const { total: protocolTotal, count: protocolCount } = calculateTotal();
  const selectedFee = schedules.find(f => f.id === selectedFeeScheduleId);
  const calculatedEventTotal = selectedFee ? selectedFee.amount * (parseInt(quantity) || 1) : 0;

  return (
    <div className="space-y-6">
      {/* Protocol Fee Schedule Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <DollarSign className="h-5 w-5" />
              <span>Protocol Fee Schedule</span>
              {schedules.length > 0 && <Badge variant="secondary">{schedules.length} Items</Badge>}
              <TooltipHelp 
                type="help"
                content="Fee items configured for this trial. Auto-trigger fees generate billing events automatically when patient status changes. Manual fees require you to add events manually."
              />
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button size="sm" variant="outline" onClick={() => setShowBudgetImport(true)} className="min-h-[44px]">
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Import Sponsor Budget
              </Button>
              <Button size="sm" onClick={() => setShowProtocolWizard(true)} className="min-h-[44px]">
                <Settings className="h-4 w-4 mr-1" />
                Configure Protocol
              </Button>
            </div>
          </div>
        </CardHeader>
        <BudgetImportDialog
          open={showBudgetImport}
          onOpenChange={setShowBudgetImport}
          trialId={trialId}
          trialSiteId={trialSiteId}
        />
        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No protocol configured. Click "Configure Protocol" to set up billable events.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Auto-Trigger</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Badge className={getCategoryColor(schedule.fee_category)}>
                        {schedule.fee_category}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{schedule.fee_name}</TableCell>
                    <TableCell className="font-semibold text-primary">
                      ${schedule.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={schedule.event_trigger_type === 'manual_only' ? 'outline' : 'default'}>
                        {schedule.event_trigger_type.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={schedule.is_active ? 'default' : 'secondary'}>
                        {schedule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Billable Events Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <span>Billable Events for This Trial</span>
              <TooltipHelp 
                type="help"
                content="Events that will be included in invoices to the sponsor. Auto-generated events appear here when patient status changes match configured fee triggers. Add manual events for ad-hoc billing."
              />
            </CardTitle>
            <Button size="sm" onClick={handleAddEventClick} disabled={schedules.length === 0} className="w-full sm:w-auto min-h-[44px]">
              <Plus className="h-4 w-4 mr-1" />
              Add Manual Event
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {trialEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No billable events recorded yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Fee Item</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trialEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{new Date(event.event_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {event.patient?.first_name} {event.patient?.last_name}
                    </TableCell>
                    <TableCell>{event.fee_schedule?.fee_name}</TableCell>
                    <TableCell className="font-semibold">
                      ${event.total_amount?.toFixed(2) || event.unit_amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={event.status === 'invoiced' ? 'default' : 'secondary'}>
                        {event.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Protocol Configuration Wizard Dialog */}
      <Dialog open={showProtocolWizard} onOpenChange={handleWizardClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Protocol Fee Configuration</DialogTitle>
            <DialogDescription>
              Configure all billable events for <strong>{trialTitle}</strong>. 
              <span className="block mt-1 text-xs">
                ℹ️ Events marked with auto-triggers (like "enrollment_milestone") will automatically 
                create billing records when patients progress through those stages. 
                Manual items require you to add billing events yourself.
              </span>
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleProtocolSubmit} className="space-y-4">
            {renderFeeSection(
              "VISITS",
              "Auto-billed when visit is completed",
              <Calendar className="h-5 w-5 text-primary" />,
              visits,
              setVisits,
              "visit"
            )}

            {renderFeeSection(
              "MILESTONES",
              "Auto-billed on patient status change",
              <TrendingUp className="h-5 w-5 text-primary" />,
              milestones,
              setMilestones,
              "milestone"
            )}

            {renderFeeSection(
              "PROCEDURES",
              "Manual billing only",
              <Activity className="h-5 w-5 text-primary" />,
              procedures,
              setProcedures,
              "procedure"
            )}

            {renderFeeSection(
              "PER DIEM",
              "Daily rates and other fees",
              <DollarSign className="h-5 w-5 text-primary" />,
              perDiem,
              setPerDiem,
              "per_diem"
            )}

            <Card className="p-4 bg-muted">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Protocol Summary</p>
                  <p className="font-semibold text-lg">
                    {protocolCount} items • Total: ${protocolTotal.toFixed(2)}
                  </p>
                </div>
              </div>
            </Card>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowProtocolWizard(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || protocolCount === 0}>
                {isSaving ? "Saving..." : "Save Protocol Configuration"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Manual Event Dialog */}
      <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Billable Event</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEventSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Patient *</Label>
              <Select value={selectedPatientId} onValueChange={setSelectedPatientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((patient: any) => (
                    <SelectItem key={patient.patient_id} value={patient.patient_id}>
                      {patient.profile.first_name} {patient.profile.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fee Schedule Item *</Label>
              <Select value={selectedFeeScheduleId} onValueChange={setSelectedFeeScheduleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select fee item" />
                </SelectTrigger>
                <SelectContent>
                  {schedules.map((fee) => (
                    <SelectItem key={fee.id} value={fee.id}>
                      {fee.fee_name} ({fee.fee_category}) - ${fee.amount.toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Date *</Label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                />
              </div>
            </div>

            {selectedFee && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">
                  Total: ${calculatedEventTotal.toFixed(2)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddEvent(false)}
                disabled={isLoadingEvent}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoadingEvent}>
                {isLoadingEvent ? "Creating..." : "Create Event"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
